/* eslint-disable max-len */
import { MetadataContent } from '@jutro/legacy/uiconfig';
import React, { useEffect, useContext, useCallback, useMemo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { useTranslator } from '@jutro/locale';
import metadata from './MLAgentQuoteStickyComponent.metadata.json5';
import styles from './MLAgentQuoteStickyComponent.module.scss';
import messages from './MLAgentQuoteStickyComponent.messages';

import { IntlContext } from '@jutro/legacy/locale';

const BOOKROLL_WITH_RATE_CAPPING = 'with_Rate_Capping';
const BOOKROLL_WITHOUT_RATE_CAPPING = 'without_ratecapping';

const MLAgentQuoteStickyComponent = (props) => {
    const {
        selectedQuote,
        submissionVM,
        getNameForMainButton,
        mainButtonFunction,
        getPerTermLabel,
        isPolicyChange,
        hasValidationErrors,
        isMainButtonDisabled
    } = props;
    const translator = useTranslator();
    const intl = useContext(IntlContext);

    const bookrollCode = _.get(submissionVM, 'bookrollInd_ML.value.code');
    const isBookroll = !isPolicyChange && (bookrollCode === BOOKROLL_WITH_RATE_CAPPING || bookrollCode === BOOKROLL_WITHOUT_RATE_CAPPING);
    const hoCoveragePartType = isPolicyChange ? _.get(selectedQuote, 'lobData.homeowners.coverables.coveragePartType_ML', '') : _.get(submissionVM, 'lobData.homeowners.coverables.coveragePartType_ML.value.code', '');
    const replacementCost = isPolicyChange ? _.get(selectedQuote, 'lobData.homeowners.coverables.constructionDetails_ML.replacementCost', 0) : _.get(submissionVM, 'lobData.homeowners.coverables.constructionDetails_ML.replacementCost.value', 0);
    const localizedErc = intl.formatNumber(replacementCost, { style: 'currency', currency: 'USD' });

    const formatAmountString = useCallback((amount) => {
        const premium = _.get(amount, 'amount', 0);
        const currency = _.get(amount, 'currency', 'usd');

        try {
            return intl.formatNumber(premium, { style: 'currency', currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const shouldDisplayHeader = (elem) => {
        const currentPosition = window.scrollY;
        const elemTop = elem.offsetTop;
        return (currentPosition >= elemTop);
    };

    const handleScrollEvent = () => {
        const stickyBar = document.getElementById('stickyParentSection');
        const proposalGrid = document.getElementById('fullProposalGrid');

        if (_.isUndefined(_.get(stickyBar, 'classList', undefined))) {
            return;
        }

        if (proposalGrid) {
            if (shouldDisplayHeader(proposalGrid)) {
                stickyBar.classList.add('show');
            } else {
                stickyBar.classList.remove('show');
            }
            return;
        }

        if (window.pageYOffset > 0 && window.pageYOffset + window.innerHeight <= document.body.scrollHeight - 300) {
            stickyBar.classList.add('show');
        } else {
            stickyBar.classList.remove('show');
        }
    };

    useEffect(() => {
        handleScrollEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScrollEvent);
        return () => {
            window.removeEventListener('scroll', handleScrollEvent);
        };
    });


    const isSelectedQuoteStale = useCallback(() => {
        return _.get(selectedQuote, 'quoteStatus.isStale', false) === true;
    }, [selectedQuote]);

    const replacementCostMessage = useMemo(() => {
        const replacementMessage = {};

        if (MLProductCodeUtil.isHomeOwnersPartialTypeRental(hoCoveragePartType)) {
            replacementMessage.message = translator(messages.ercRental, { ercVal: localizedErc});
            replacementMessage.help = translator(messages.helpRenter);
        } else if (MLProductCodeUtil.isHomeOwnersPartialTypeHome(hoCoveragePartType) || MLProductCodeUtil.isHomeOwnersPartialTypeLandlordHome(hoCoveragePartType)) {
            replacementMessage.message = translator(messages.ercNonRental, { ercVal: localizedErc});
            replacementMessage.help = translator(messages.helpHome);
        } else if (MLProductCodeUtil.isHomeOwnersPartialTypeCondo(hoCoveragePartType) || MLProductCodeUtil.isHomeOwnersPartialTypeLandlordCondo(hoCoveragePartType)) {
            replacementMessage.message = translator(messages.ercNonRental, { ercVal: localizedErc});
            replacementMessage.help = translator(messages.helpCondo);
        }

        return replacementMessage;
    }, [hoCoveragePartType, localizedErc, translator]);


    const overrides = {
        stateFeesComp: {
            visible: _.get(selectedQuote, isPolicyChange ? 'totalFullTermChargesRPT_Ext.amount' : 'totalChanges_Ext.amount', 0) > 0,
            totalFees: _.get(selectedQuote, isPolicyChange ? 'totalFullTermChargesRPT_Ext': 'totalChanges_Ext', {}),
            itemizedFees: _.get(selectedQuote, isPolicyChange ? 'fullTermFeesCosts_Ext' : 'feesCosts_Ext', []),
            displayAlternativeTemplate: true
        },
        replacementCostP: {
            visible: !_.isEmpty(replacementCostMessage)
        },
        replacementCostTooltip: {
            tooltipLabel: replacementCostMessage.message,
            tooltipContent: replacementCostMessage.help,
            noHeader: true
        },
        nonPolicyChangeButton: {
            visible: !isPolicyChange,
            content: getNameForMainButton(),
            onClick: mainButtonFunction,
            disabled: isMainButtonDisabled()
        },
        policyChangeButton: {
            visible: isPolicyChange,
            content: getNameForMainButton(),
            onClick: mainButtonFunction,
            disabled: isMainButtonDisabled()
        },
        nonPolicyChangePremiumPrice: {
            visible: !isPolicyChange,
            content: `${formatAmountString(_.get(selectedQuote, 'selectedPaymentPlan.total'))}/${getPerTermLabel(selectedQuote.termType_ML)}`,
            className: (isSelectedQuoteStale() || hasValidationErrors(selectedQuote)) && !isBookroll ? 'total strikethrough' : 'total'
        },
        policyChangePremiumPrice: {
            visible: isPolicyChange,
            content: `${formatAmountString(_.get(selectedQuote, 'fullTermCost_Ext'))}/${getPerTermLabel(selectedQuote.termType_ML)}`,
            className: isSelectedQuoteStale() ? 'total strikethrough' : 'total'
        },
    };

    const resolvers = {
        resolveCallbackMap: {
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
};

MLAgentQuoteStickyComponent.propTypes = {
    selectedQuote: PropTypes.shape({}),
    submissionVM: PropTypes.shape({}),
    isQuoteLocked: PropTypes.bool,
    forPostQuote: PropTypes.bool,
    isBundle: PropTypes.bool,
    isPurchasePolicyButton: PropTypes.bool,
    canIssuePolicy: PropTypes.bool,
    getNameForMainButton: PropTypes.func,
    mainButtonFunction: PropTypes.func,
    getPerTermLabel: PropTypes.func,
    isPolicyChange: PropTypes.bool,
    hasValidationErrors: PropTypes.func,
    isMainButtonDisabled: PropTypes.func
};

MLAgentQuoteStickyComponent.defaultProps = {
    hasValidationErrors: _.noop
}

export default MLAgentQuoteStickyComponent;